import { useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react'
import { Command } from 'cmdk'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { ComboboxList, type ComboboxItem } from '../Combobox/Combobox'
import { Popover, PopoverAnchor, PopoverContent } from '../Popover/Popover'

export interface SearchSelectProps {
  /** Result rows, already mapped by the consumer (server- or client-search). */
  items: ComboboxItem[]
  /** The selected item's cmdk value, for highlight in the list. */
  value?: string
  /** Label shown in the field for the current selection. */
  selectedLabel?: string | null
  /** Fires with the chosen item's value on select. */
  onSelect: (value: string) => void
  /** Fires when the clear (×) affordance is used. */
  onClear?: () => void
  /**
   * Fires with the live typed text on every keystroke. The consumer drives
   * its search query (and re-supplies `items`) from this. The visible field's
   * displayed text is owned internally so a blurred field shows the committed
   * `selectedLabel`, not the last search string.
   */
  onQueryChange: (q: string) => void
  /** Field placeholder, e.g. "Search a company or a person…". */
  placeholder?: string
  emptyMessage?: string
  /**
   * `false` disables cmdk's client filter (server-search — items already
   * filtered upstream). `true` lets cmdk fuzzy-filter the supplied items by
   * the typed text (small cached lists). Defaults to `false`.
   */
  shouldFilter?: boolean
  /**
   * Width of the floating results panel. Defaults to the field width via
   * Radix's `--radix-popover-trigger-width`.
   */
  width?: number
  /**
   * Auto-focus + auto-open on mount; routes Esc / outside-click to
   * `onCancel`. Used by `EditableGrid.renderEditor` (the cell is already in
   * edit mode — the operator shouldn't have to click a second time).
   */
  editorMode?: boolean
  onCancel?: () => void
  /** Show the clear (×) affordance when a value/text is present. Default true. */
  clearable?: boolean
  disabled?: boolean
  /**
   * `'cell'` — bare, full-height styling for an `EditableGrid` cell.
   * `'field'` — bordered form-field styling (mirrors the `Input` primitive).
   * Defaults to `'field'`.
   */
  variant?: 'cell' | 'field'
  /** Extra classes on the field wrapper. */
  className?: string
  /**
   * Leading icon. Defaults to a magnifying glass (`Search`) — override when
   * the field sits next to another search-style input and the shared icon
   * would read as a duplicate control rather than a distinct filter (e.g.
   * Product Customer Pricing's salesperson filter next to the plain
   * customer search box uses `User` instead).
   */
  icon?: ComponentType<{ size?: number; className?: string; 'aria-hidden'?: boolean }>
  /** Optional content rendered under the results list (e.g. "20 of 362 — keep typing"). */
  footer?: ReactNode
}

/**
 * Input-first search picker: the visible field IS the search input — type
 * directly, results drop down in a floating (portaled) list as you type. No
 * click-to-open dropdown. Built by composing one cmdk `<Command>` whose
 * `Command.Input` lives inline on the popover anchor while the `ComboboxList`
 * renders in a portaled `PopoverContent` (escapes drawer / grid-cell
 * overflow at `z-popover`). cmdk keyboard nav (↑↓/Enter) works natively
 * because the focused input sits inside `cmdk-root`; Esc / outside-click
 * dismiss via Radix.
 *
 * The five FK pickers (`ProductPicker`, `PartnerPicker`, `CountryPicker`,
 * `IngredientPicker`, `CustomerContactPicker`) delegate their trigger + panel
 * here so the input-first behavior lives in exactly one place.
 */
export function SearchSelect({
  items,
  value,
  selectedLabel,
  onSelect,
  onClear,
  onQueryChange,
  placeholder = 'Search…',
  emptyMessage = 'Type to search',
  shouldFilter = false,
  width,
  editorMode = false,
  onCancel,
  clearable = true,
  disabled = false,
  variant = 'field',
  className,
  icon: Icon = Search,
  footer,
}: SearchSelectProps) {
  const [open, setOpen] = useState<boolean>(editorMode)
  // Visible field text. Decoupled from the live search string so a blurred
  // field shows the committed selection while typing drives the search.
  const [text, setText] = useState<string>(selectedLabel ?? '')
  const inputRef = useRef<HTMLInputElement>(null)

  // Reflect external selection changes (incl. the commit from onSelect) back
  // into the field by adjusting state during render when the committed label
  // changes — the React-recommended alternative to a sync effect. Only runs
  // when the label actually changes, so it never clobbers in-progress typing.
  const [prevLabel, setPrevLabel] = useState(selectedLabel)
  if (selectedLabel !== prevLabel) {
    setPrevLabel(selectedLabel)
    setText(selectedLabel ?? '')
  }

  // editorMode (grid cell): focus immediately so the operator types at once.
  useEffect(() => {
    if (editorMode) inputRef.current?.focus()
  }, [editorMode])

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      // Dismissed without a pick (outside-click / Esc): discard the in-progress
      // query and restore the committed label.
      setText(selectedLabel ?? '')
      if (editorMode) onCancel?.()
    }
  }

  const handleSelect = (val: string) => {
    onSelect(val)
    setOpen(false)
    inputRef.current?.blur()
  }

  const handleClear = () => {
    onClear?.()
    setText('')
    onQueryChange('')
    inputRef.current?.focus()
  }

  const isCell = variant === 'cell'
  const showClear = clearable && !disabled && (text.length > 0 || !!selectedLabel)

  return (
    <Command
      shouldFilter={shouldFilter}
      className={cn('relative w-full', isCell && 'h-full')}
    >
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverAnchor asChild>
          <div
            className={cn(
              'flex w-full items-center gap-1.5 text-primary',
              isCell
                ? 'h-full border-none bg-transparent px-[var(--cell-padding-x)] text-sm'
                : [
                    'h-[var(--input-height)] rounded-sm border border-border-default bg-surface px-2.5 text-md',
                    'transition-[border-color,box-shadow] duration-fast',
                    'hover:border-border-strong',
                    'focus-within:border-border-focus focus-within:shadow-focus',
                    disabled && 'cursor-not-allowed bg-surface-sunk text-text-disabled border-border-subtle',
                  ],
              className,
            )}
          >
            <Icon
              aria-hidden={true}
              size={isCell ? 12 : 14}
              className="flex-shrink-0 text-tertiary"
            />
            <Command.Input
              ref={inputRef}
              value={text}
              disabled={disabled}
              placeholder={placeholder}
              aria-label={placeholder}
              onValueChange={(v) => {
                setText(v)
                onQueryChange(v)
                if (!open) setOpen(true)
              }}
              onFocus={(e) => {
                setOpen(true)
                e.currentTarget.select()
              }}
              className={cn(
                'min-w-0 flex-1 border-none bg-transparent p-0 text-inherit outline-none',
                'placeholder:text-tertiary disabled:cursor-not-allowed',
              )}
            />
            {showClear ? (
              <button
                type="button"
                aria-label="Clear selection"
                tabIndex={-1}
                // Keep focus on the input so the click registers as a clear,
                // not a dismiss.
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleClear}
                className="flex flex-shrink-0 items-center text-tertiary hover:text-primary"
              >
                <X size={isCell ? 12 : 14} />
              </button>
            ) : null}
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="p-0"
          style={{ width: width ?? 'var(--radix-popover-trigger-width)' }}
          // Keep focus on the visible input so typing continues uninterrupted.
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <ComboboxList
            items={items}
            value={value}
            onChange={handleSelect}
            emptyMessage={emptyMessage}
          />
          {footer ? (
            <div className="border-t border-border-subtle px-3 py-1.5 text-xs text-tertiary">
              {footer}
            </div>
          ) : null}
        </PopoverContent>
      </Popover>
    </Command>
  )
}
